import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    FiActivity,
    FiAlertTriangle,
    FiCheck,
    FiClock,
    FiMic,
    FiMicOff,
    FiRefreshCw,
    FiX,
} from "react-icons/fi";
import { buildAssistantHeaders, buildAssistantUrl } from "../../utils/assistantApi";
import Tooltip from "../Tooltip";
import "./VoiceInputControl.scss";

const WAVEFORM_SHAPE = [
    0.04, 0.05, 0.06, 0.08, 0.1, 0.14, 0.22, 0.38, 0.78, 0.42, 0.64, 0.92,
    0.5, 0.76, 0.46, 0.88, 0.58, 0.7, 0.42, 0.28, 0.2, 0.16, 0.12, 0.09,
];

const WAVEFORM_BARS = WAVEFORM_SHAPE.map((shapeValue) =>
    Math.round(3 + shapeValue * 18),
);

const VOICE_STATUS = {
    IDLE: "idle",
    LISTENING: "listening",
    LIMIT_REACHED: "limit_reached",
    TRANSCRIBING: "transcribing",
    MIC_BLOCKED: "mic_blocked",
    MIC_UNAVAILABLE: "mic_unavailable",
    TRANSCRIPTION_FAILED: "transcription_failed",
};

const VOICE_MIME_TYPE = "audio/webm;codecs=opus";
const VOICE_TRANSCRIPTION_TIMEOUT_MS = 50000;
const VOICE_RECORDING_MAX_DURATION_MS = 60000;
const VOICE_RECORDING_WARNING_REMAINING_MS = 5000;
const VOICE_LIMIT_REACHED_DISPLAY_MS = 700;
const VOICE_RECORDING_GAIN = 2.5;
const IS_VOICE_DEBUG_ENABLED =
    import.meta.env.VITE_AI_ASSISTANT_VOICE_DEBUG_ENABLED === "true";
const MIC_AUDIO_CONSTRAINTS = {
    audio: {
        autoGainControl: true,
        echoCancellation: true,
        noiseSuppression: true,
    },
};

const formatVoiceDuration = (durationMs) => {
    const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes.toString().padStart(2, "0")}:${seconds
        .toString()
        .padStart(2, "0")}`;
};

const wait = (durationMs) =>
    new Promise((resolve) => {
        window.setTimeout(resolve, durationMs);
    });

const getVoiceClassName = (...classNames) => classNames.filter(Boolean).join(" ");

const VoiceWaveform = ({ tone = "blue", levels = WAVEFORM_BARS, processing = false }) => (
    <div
        className={getVoiceClassName(
            "voice-waveform",
            `voice-waveform--${tone}`,
            processing && "voice-waveform--processing",
        )}
        aria-hidden="true"
    >
        {levels.map((height, index) => (
            <span
                key={`voice-wave-${index}`}
                style={{
                    "--voice-wave-index": index,
                    height: `${height}px`,
                    opacity: Math.min(1, 0.48 + height / 34),
                }}
            />
        ))}
    </div>
);

const VoiceInputControl = ({
    disabled = false,
    onTranscript,
    onActiveChange,
    onStatusChange,
    panelHostRef,
}) => {
    const [status, setStatus] = useState(VOICE_STATUS.IDLE);
    const [audioLevels, setAudioLevels] = useState(WAVEFORM_BARS);
    const [debugRecording, setDebugRecording] = useState(null);
    const [permissionGuideOpen, setPermissionGuideOpen] = useState(false);
    const [isMicPermissionDenied, setIsMicPermissionDenied] = useState(false);
    const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);

    const finalTranscriptRef = useRef("");
    const isConfirmingRef = useRef(false);
    const timeoutRef = useRef(null);
    const recordingLimitTimeoutRef = useRef(null);
    const recordingElapsedIntervalRef = useRef(null);
    const statusRef = useRef(status);
    const audioContextRef = useRef(null);
    const audioFrameRef = useRef(null);
    const audioStreamRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const recordingStartedAtRef = useRef(null);
    const recordingSessionIdRef = useRef(0);
    const debugRecordingUrlRef = useRef(null);
    const permissionGuideRef = useRef(null);

    const isVoiceActive = status !== VOICE_STATUS.IDLE;

    const isVoiceInputSupported =
        typeof window !== "undefined" &&
        Boolean(navigator.mediaDevices?.getUserMedia) &&
        typeof MediaRecorder !== "undefined";

    const panelTone =
        status === VOICE_STATUS.LIMIT_REACHED
            ? "red"
            : status === VOICE_STATUS.TRANSCRIBING
            ? "green"
            : status === VOICE_STATUS.TRANSCRIPTION_FAILED
              ? "orange"
            : status === VOICE_STATUS.MIC_BLOCKED ||
                status === VOICE_STATUS.MIC_UNAVAILABLE
              ? "red"
              : "purple";

    const isErrorState =
        status === VOICE_STATUS.MIC_BLOCKED ||
        status === VOICE_STATUS.MIC_UNAVAILABLE ||
        status === VOICE_STATUS.TRANSCRIPTION_FAILED;

    const statusLabel = {
        [VOICE_STATUS.LISTENING]: "Listening...",
        [VOICE_STATUS.LIMIT_REACHED]: "Time limit reached",
        [VOICE_STATUS.TRANSCRIBING]: "Lou is transcribing...",
        [VOICE_STATUS.MIC_BLOCKED]: "Microphone access blocked",
        [VOICE_STATUS.MIC_UNAVAILABLE]: "Microphone is unavailable",
        [VOICE_STATUS.TRANSCRIPTION_FAILED]: "Couldn't transcribe audio",
    }[status];

    const statusDescription = {
        [VOICE_STATUS.MIC_BLOCKED]: "Allow microphone access to dictate to Lou.",
        [VOICE_STATUS.MIC_UNAVAILABLE]: "Please check your system microphone permission and try again.",
        [VOICE_STATUS.TRANSCRIPTION_FAILED]: "Please try again or check your connection.",
    }[status];

    const ErrorStatusIcon = {
        [VOICE_STATUS.MIC_BLOCKED]: FiAlertTriangle,
        [VOICE_STATUS.MIC_UNAVAILABLE]: FiAlertTriangle,
        [VOICE_STATUS.TRANSCRIPTION_FAILED]: FiX,
    }[status] || FiAlertTriangle;

    const isTranscribing = status === VOICE_STATUS.TRANSCRIBING;
    const isListening = status === VOICE_STATUS.LISTENING;
    const isLimitReached = status === VOICE_STATUS.LIMIT_REACHED;
    const shouldShowRecordingTimer = isListening || isLimitReached;
    const cappedRecordingElapsedMs = Math.min(recordingElapsedMs, VOICE_RECORDING_MAX_DURATION_MS);
    const recordingRemainingMs = Math.max(
        0,
        VOICE_RECORDING_MAX_DURATION_MS - cappedRecordingElapsedMs,
    );
    const isRecordingNearLimit =
        isListening && recordingRemainingMs <= VOICE_RECORDING_WARNING_REMAINING_MS;
    const displayedAudioLevels = isTranscribing ? WAVEFORM_BARS : audioLevels;

    // Held in refs so these fire when the value changes, not when the parent
    // re-renders. A callback declared inline is a new function every render,
    // which made this notify, set state, re-render and notify again.
    const onActiveChangeRef = useRef(onActiveChange);
    const onStatusChangeRef = useRef(onStatusChange);
    useEffect(() => {
        onActiveChangeRef.current = onActiveChange;
        onStatusChangeRef.current = onStatusChange;
    }, [onActiveChange, onStatusChange]);

    useEffect(() => {
        onActiveChangeRef.current?.(isVoiceActive);
    }, [isVoiceActive]);

    useEffect(() => {
        statusRef.current = status;
        onStatusChangeRef.current?.(status);
    }, [status]);

    const getMicrophonePermissionState = async () => {
        try {
            return (await navigator.permissions?.query?.({ name: "microphone" }))?.state || null;
        } catch {
            return null;
        }
    };

    useEffect(() => {
        let permissionStatus;
        let handlePermissionChange;
        let isMounted = true;

        const syncMicrophonePermission = async () => {
            try {
                permissionStatus = await navigator.permissions?.query?.({ name: "microphone" });

                if (!isMounted || !permissionStatus) return;

                handlePermissionChange = () => {
                    setIsMicPermissionDenied(permissionStatus.state === "denied");
                };

                handlePermissionChange();
                permissionStatus.addEventListener?.("change", handlePermissionChange);
            } catch {
                setIsMicPermissionDenied(false);
            }
        };

        syncMicrophonePermission();

        return () => {
            isMounted = false;
            if (permissionStatus && handlePermissionChange) {
                permissionStatus.removeEventListener?.("change", handlePermissionChange);
            }
        };
    }, []);

    useEffect(() => {
        if (!permissionGuideOpen) return undefined;

        const handleOutsideClick = (event) => {
            if (permissionGuideRef.current?.contains(event.target)) return;
            setPermissionGuideOpen(false);
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [permissionGuideOpen]);

    useEffect(
        () => () => {
            window.clearTimeout(timeoutRef.current);
            window.clearTimeout(recordingLimitTimeoutRef.current);
            window.clearInterval(recordingElapsedIntervalRef.current);
            recordingSessionIdRef.current += 1;
            closeRecorder();
            stopAudioLevelTracking();

            if (debugRecordingUrlRef.current) {
                URL.revokeObjectURL(debugRecordingUrlRef.current);
                debugRecordingUrlRef.current = null;
            }
        },
        [],
    );

    const setDebugAudioBlob = (audioBlob, metadata = {}) => {
        if (!IS_VOICE_DEBUG_ENABLED) return;

        if (debugRecordingUrlRef.current) {
            URL.revokeObjectURL(debugRecordingUrlRef.current);
        }

        const nextUrl = URL.createObjectURL(audioBlob);
        debugRecordingUrlRef.current = nextUrl;

        setDebugRecording({
            url: nextUrl,
            bytes: audioBlob.size,
            mimeType: audioBlob.type,
            ...metadata,
        });
    };

    const clearDebugRecording = () => {
        if (debugRecordingUrlRef.current) {
            URL.revokeObjectURL(debugRecordingUrlRef.current);
            debugRecordingUrlRef.current = null;
        }

        setDebugRecording(null);
    };

    const closeRecorder = () => {
        if (mediaRecorderRef.current?.state === "recording") {
            mediaRecorderRef.current.stop();
        }

        mediaRecorderRef.current = null;
    };

    const stopRecorderAndBuildBlob = () => {
        const recorder = mediaRecorderRef.current;

        return new Promise((resolve, reject) => {
            if (!recorder) {
                resolve(new Blob(audioChunksRef.current, { type: VOICE_MIME_TYPE }));
                return;
            }

            recorder.ondataavailable = (event) => {
                if (event.data?.size) {
                    audioChunksRef.current.push(event.data);

                    if (IS_VOICE_DEBUG_ENABLED) {
                        console.info("[AI Assistant] Voice recording chunk", {
                            chunkBytes: event.data.size,
                            chunkCount: audioChunksRef.current.length,
                            elapsedMs: recordingStartedAtRef.current
                                ? Math.round(performance.now() - recordingStartedAtRef.current)
                                : null,
                        });
                    }
                }
            };

            recorder.onerror = () => reject(new Error("Audio recorder failed"));

            recorder.onstop = () => {
                mediaRecorderRef.current = null;
                resolve(new Blob(audioChunksRef.current, { type: recorder.mimeType || VOICE_MIME_TYPE }));
            };

            if (recorder.state === "recording") {
                recorder.stop();
            } else {
                mediaRecorderRef.current = null;
                resolve(new Blob(audioChunksRef.current, { type: recorder.mimeType || VOICE_MIME_TYPE }));
            }
        });
    };

    const stopAudioLevelTracking = () => {
        if (audioFrameRef.current) {
            window.cancelAnimationFrame(audioFrameRef.current);
            audioFrameRef.current = null;
        }

        audioStreamRef.current?.getTracks?.().forEach((track) => track.stop());
        audioStreamRef.current = null;

        audioContextRef.current?.close?.();
        audioContextRef.current = null;

        setAudioLevels(WAVEFORM_BARS);
    };

    const stopRecordingTimers = () => {
        window.clearTimeout(recordingLimitTimeoutRef.current);
        window.clearInterval(recordingElapsedIntervalRef.current);
        recordingLimitTimeoutRef.current = null;
        recordingElapsedIntervalRef.current = null;
    };

    const startRecordingTimers = () => {
        stopRecordingTimers();
        setRecordingElapsedMs(0);

        recordingElapsedIntervalRef.current = window.setInterval(() => {
            if (!recordingStartedAtRef.current) return;

            setRecordingElapsedMs(
                Math.min(
                    performance.now() - recordingStartedAtRef.current,
                    VOICE_RECORDING_MAX_DURATION_MS,
                ),
            );
        }, 250);

        recordingLimitTimeoutRef.current = window.setTimeout(() => {
            if (mediaRecorderRef.current?.state !== "recording" || isConfirmingRef.current) return;

            console.info("[AI Assistant] Voice recording max duration reached", {
                maxDurationMs: VOICE_RECORDING_MAX_DURATION_MS,
            });

            confirmTranscript({ reachedLimit: true });
        }, VOICE_RECORDING_MAX_DURATION_MS);
    };

    const startAudioLevelTracking = async (stream) => {
        stopAudioLevelTracking();
        audioStreamRef.current = stream;

        try {
            const AudioContextApi = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextApi) return;

            const audioContext = new AudioContextApi();
            const source = audioContext.createMediaStreamSource(stream);
            const gainNode = audioContext.createGain();
            const analyser = audioContext.createAnalyser();

            gainNode.gain.value = VOICE_RECORDING_GAIN;
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.55;

            source.connect(gainNode);
            gainNode.connect(analyser);

            const dataArray = new Uint8Array(analyser.fftSize);

            audioContextRef.current = audioContext;

            let smoothedLevel = 0;
            const startedAt = performance.now();

            const updateLevels = () => {
                analyser.getByteTimeDomainData(dataArray);

                let sumSquares = 0;

                for (let index = 0; index < dataArray.length; index += 1) {
                    const centeredValue = (dataArray[index] - 128) / 128;
                    sumSquares += centeredValue * centeredValue;
                }

                const rms = Math.sqrt(sumSquares / dataArray.length);
                const normalizedLevel = Math.min(1, rms * 13.5);

                smoothedLevel = smoothedLevel * 0.42 + normalizedLevel * 0.58;

                const elapsed = (performance.now() - startedAt) / 160;

                const maxIndex = Math.max(1, WAVEFORM_SHAPE.length - 1);

                const nextLevels = WAVEFORM_SHAPE.map((shapeValue, index) => {
                    const position = index / maxIndex;
                    const outwardPhase = elapsed - position * 3.2;
                    const shimmer = (Math.sin(outwardPhase) + 1) * 0.5;
                    const ripple = (Math.sin(outwardPhase * 1.2 - 0.6) + 1) * 0.5;
                    const centerBias = 1 + Math.max(0, 0.12 - Math.abs(position - 0.5) * 0.18);
                    const idleHeight = 3 + shapeValue * (2 + shimmer * 3);
                    const voiceHeight =
                        3 +
                        shapeValue *
                            centerBias *
                            (7 + smoothedLevel * 15 + shimmer * 3 + ripple * smoothedLevel * 7);

                    return Math.round(Math.max(idleHeight, voiceHeight));
                });

                setAudioLevels(nextLevels);
                audioFrameRef.current = window.requestAnimationFrame(updateLevels);
            };

            updateLevels();
        } catch {
            setAudioLevels(WAVEFORM_BARS);
        }
    };

    const finishTranscription = () => {
        window.clearTimeout(timeoutRef.current);
        stopRecordingTimers();

        const text = finalTranscriptRef.current.trim();

        if (text) {
            onTranscript?.(text);
        }

        finalTranscriptRef.current = "";
        isConfirmingRef.current = false;

        setStatus(VOICE_STATUS.IDLE);

        recordingStartedAtRef.current = null;
        setRecordingElapsedMs(0);
    };

    const transcribeRecording = async (audioBlob) => {
        const formData = new FormData();
        formData.append("audio", audioBlob, "voice-input.webm");

        const headers = await buildAssistantHeaders();
        delete headers["Content-Type"];

        const controller = new AbortController();

        const timeoutId = window.setTimeout(
            () => controller.abort(),
            VOICE_TRANSCRIPTION_TIMEOUT_MS,
        );

        try {
            const response = await fetch(buildAssistantUrl("voice/transcribe"), {
                method: "POST",
                headers,
                credentials: "include",
                body: formData,
                signal: controller.signal,
            });

            if (!response.ok) {
                let detail = "";

                try {
                    detail = (await response.json())?.detail || "";
                } catch {
                    detail = await response.text();
                }

                throw new Error(detail || "Voice transcription failed");
            }

            const payload = await response.json();

            return payload?.text || "";
        } finally {
            window.clearTimeout(timeoutId);
        }
    };

    const resetVoiceInput = () => {
        window.clearTimeout(timeoutRef.current);
        stopRecordingTimers();
        closeRecorder();
        stopAudioLevelTracking();

        audioChunksRef.current = [];
        recordingStartedAtRef.current = null;
        recordingSessionIdRef.current += 1;
        finalTranscriptRef.current = "";
        isConfirmingRef.current = false;

        setRecordingElapsedMs(0);
        setStatus(VOICE_STATUS.IDLE);
        setPermissionGuideOpen(false);
    };

    const startListeningWithStream = async (stream) => {
        await startAudioLevelTracking(stream);

        audioChunksRef.current = [];
        recordingStartedAtRef.current = performance.now();
        recordingSessionIdRef.current += 1;

        const mimeType = MediaRecorder.isTypeSupported(VOICE_MIME_TYPE)
            ? VOICE_MIME_TYPE
            : "audio/webm";

        const recorder = new MediaRecorder(stream, {
            audioBitsPerSecond: 128000,
            mimeType,
        });

        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
            if (event.data?.size) {
                audioChunksRef.current.push(event.data);

                if (IS_VOICE_DEBUG_ENABLED) {
                    console.info("[AI Assistant] Voice recording data available", {
                        chunkBytes: event.data.size,
                        chunkCount: audioChunksRef.current.length,
                        elapsedMs: recordingStartedAtRef.current
                            ? Math.round(performance.now() - recordingStartedAtRef.current)
                            : null,
                    });
                }
            }
        };

        recorder.start();
        startRecordingTimers();

        console.info("[AI Assistant] Voice recording started", {
            mimeType,
            visualizerGain: VOICE_RECORDING_GAIN,
            maxDurationMs: VOICE_RECORDING_MAX_DURATION_MS,
            tracks: stream.getAudioTracks().length,
        });
    };

    const showMicBlockedState = (openGuide = false) => {
        setIsMicPermissionDenied(true);
        setPermissionGuideOpen(openGuide);
        setStatus(VOICE_STATUS.MIC_BLOCKED);
    };

    const handleMicStartFailure = (error) => {
        console.error("[AI Assistant] Voice input permission failed", error);

        stopAudioLevelTracking();
        closeRecorder();

        if (["NotFoundError", "NotReadableError", "AbortError", "OverconstrainedError"].includes(error?.name)) {
            setStatus(VOICE_STATUS.MIC_UNAVAILABLE);
            return;
        }

        showMicBlockedState(true);
    };

    const requestMicAndStartListening = async () => {
        if (disabled || !isVoiceInputSupported) return;

        const permissionState = await getMicrophonePermissionState();

        if (permissionState === "denied" || (isMicPermissionDenied && permissionState === null)) {
            showMicBlockedState(true);
            return;
        }

        finalTranscriptRef.current = "";
        isConfirmingRef.current = false;

        setPermissionGuideOpen(false);
        setStatus(VOICE_STATUS.LISTENING);

        try {
            const stream = await navigator.mediaDevices.getUserMedia(MIC_AUDIO_CONSTRAINTS);
            setIsMicPermissionDenied(false);
            await startListeningWithStream(stream);
        } catch (error) {
            handleMicStartFailure(error);
        }
    };

    const startListening = () => {
        requestMicAndStartListening();
    };

    const handleMicSettingsClick = async () => {
        if (disabled || !isVoiceInputSupported) return;

        try {
            const permission = await navigator.permissions?.query?.({ name: "microphone" });

            if (permission?.state === "denied") {
                showMicBlockedState(true);
                return;
            }

            await requestMicAndStartListening();
        } catch (error) {
            handleMicStartFailure(error);
        }
    };

    const confirmTranscript = async ({ reachedLimit = false } = {}) => {
        if (statusRef.current !== VOICE_STATUS.LISTENING || isConfirmingRef.current) return;

        isConfirmingRef.current = true;
        const recordingSessionId = recordingSessionIdRef.current;
        window.clearTimeout(timeoutRef.current);
        stopRecordingTimers();
        setRecordingElapsedMs((currentElapsedMs) =>
            reachedLimit ? VOICE_RECORDING_MAX_DURATION_MS : currentElapsedMs,
        );
        setStatus(reachedLimit ? VOICE_STATUS.LIMIT_REACHED : VOICE_STATUS.TRANSCRIBING);

        try {
            const audioBlob = await stopRecorderAndBuildBlob();

            stopAudioLevelTracking();

            if (reachedLimit) {
                await wait(VOICE_LIMIT_REACHED_DISPLAY_MS);
                if (recordingSessionIdRef.current !== recordingSessionId) return;
                setStatus(VOICE_STATUS.TRANSCRIBING);
            }

            if (!audioBlob.size) {
                throw new Error("No recorded audio was captured");
            }

            const durationMs = recordingStartedAtRef.current
                ? Math.round(performance.now() - recordingStartedAtRef.current)
                : null;

            console.info("[AI Assistant] Voice recording ready", {
                audioBytes: audioBlob.size,
                durationMs,
                chunkCount: audioChunksRef.current.length,
                mimeType: audioBlob.type,
            });

            setDebugAudioBlob(audioBlob, {
                durationMs,
                chunkCount: audioChunksRef.current.length,
            });

            const text = await transcribeRecording(audioBlob);

            finalTranscriptRef.current = text;
            finishTranscription();
        } catch (error) {
            console.error("[AI Assistant] Voice transcription failed", error);

            stopAudioLevelTracking();
            closeRecorder();

            setStatus(VOICE_STATUS.TRANSCRIPTION_FAILED);
        }
    };

    const voicePanel = isVoiceActive ? (
        <div className={getVoiceClassName("voice-input-control__panel", `voice-input-control__panel--${panelTone}`)}>
            {!isErrorState && (
                <div
                    className={getVoiceClassName(
                        "voice-input-control__visualizer",
                        isTranscribing && "voice-input-control__visualizer--transcribing",
                        isRecordingNearLimit && "voice-input-control__visualizer--warning",
                        isLimitReached && "voice-input-control__visualizer--limit",
                    )}
                >
                    <div className="voice-input-control__wave voice-input-control__wave--left">
                        <VoiceWaveform
                            tone={panelTone}
                            levels={displayedAudioLevels}
                            processing={isTranscribing}
                        />
                    </div>

                    <div
                        className={getVoiceClassName(
                            "voice-input-control__status",
                            isRecordingNearLimit && "voice-input-control__status--warning",
                            isLimitReached && "voice-input-control__status--limit",
                        )}
                        role="status"
                        aria-live="polite"
                    >
                        <span
                            className={getVoiceClassName(
                                "voice-input-control__status-icon",
                                "voice-input-control__status-icon--active",
                                isTranscribing && "voice-input-control__status-icon--processing",
                            )}
                        >
                            {isTranscribing ? (
                                <FiActivity className="voice-input-control__pulse-icon" />
                            ) : isLimitReached ? (
                                <FiClock />
                            ) : (
                                <FiMic />
                            )}
                        </span>
                        <span>{statusLabel}</span>
                        {shouldShowRecordingTimer && (
                            <span className="voice-input-control__timer" aria-label="Voice recording timer">
                                <strong>{formatVoiceDuration(cappedRecordingElapsedMs)}</strong>
                                <span>/</span>
                                <span>{formatVoiceDuration(VOICE_RECORDING_MAX_DURATION_MS)}</span>
                            </span>
                        )}
                        {isRecordingNearLimit && (
                            <>
                                <span className="voice-input-control__limit-warning">
                                    <FiAlertTriangle />
                                    {Math.ceil(recordingRemainingMs / 1000)} seconds remaining
                                </span>
                                <small className="voice-input-control__status-note">
                                    Recording will stop automatically at 60 seconds.
                                </small>
                            </>
                        )}
                        {isLimitReached && (
                            <small className="voice-input-control__status-note">
                                Recording stopped automatically.
                            </small>
                        )}
                    </div>

                    <div className="voice-input-control__wave voice-input-control__wave--right">
                        <VoiceWaveform
                            tone={panelTone}
                            levels={[...displayedAudioLevels].reverse()}
                            processing={isTranscribing}
                        />
                    </div>
                </div>
            )}

            {isErrorState && (
                <div
                    className="voice-input-control__status voice-input-control__status--message"
                    role="status"
                    aria-live="polite"
                >
                    <span className="voice-input-control__status-icon">
                        <ErrorStatusIcon />
                    </span>
                    <span className="voice-input-control__status-copy">
                        <span>{statusLabel}</span>
                        <small>{statusDescription}</small>
                    </span>
                </div>
            )}
        </div>
    ) : null;

    return (
        <div className="voice-input-control">
            {voicePanel && (
                panelHostRef?.current
                    ? createPortal(voicePanel, panelHostRef.current)
                    : voicePanel
            )}

            {permissionGuideOpen && (
                <div
                    ref={permissionGuideRef}
                    className="voice-input-control__permission-guide"
                    role="dialog"
                    aria-label="Microphone permission help"
                >
                    <div className="voice-input-control__permission-title">
                        Enable microphone access
                    </div>
                    <p className="voice-input-control__permission-description">
                        To use voice input, allow microphone access for this site.
                    </p>
                    <div className="voice-input-control__permission-steps">
                        {[
                            "Click the icon in the browser address bar",
                            "Open Site Settings",
                            "Set Microphone to Allow",
                        ].map((step, index) => (
                            <div className="voice-input-control__permission-step" key={step}>
                                <span>{index + 1}</span>
                                <p>{step}</p>
                            </div>
                        ))}
                    </div>
                    <div className="voice-input-control__permission-actions">
                        <button type="button" onClick={() => setPermissionGuideOpen(false)}>
                            Dismiss
                        </button>
                        <button
                            type="button"
                            className="voice-input-control__permission-retry"
                            onClick={requestMicAndStartListening}
                        >
                            Try again
                        </button>
                    </div>
                </div>
            )}

            {isVoiceActive && (
                <div className="voice-input-control__actions voice-input-control__actions--inline">
                    <Tooltip content="Stop dictation" placement="bottom">
                        <button
                            type="button"
                            className="voice-input-control__action"
                            onClick={resetVoiceInput}
                            aria-label="Stop dictation"
                        >
                            <FiX />
                        </button>
                    </Tooltip>

                    {status === VOICE_STATUS.MIC_BLOCKED && (
                        <Tooltip content="Enable microphone for Lou" placement="bottom">
                            <button
                                type="button"
                                className="voice-input-control__action voice-input-control__action--settings"
                                onClick={handleMicSettingsClick}
                                aria-label="Enable microphone for Lou"
                            >
                                <FiMicOff />
                            </button>
                        </Tooltip>
                    )}

                    {status === VOICE_STATUS.TRANSCRIPTION_FAILED && (
                        <Tooltip content="Try dictation again" placement="bottom">
                            <button
                                type="button"
                                className="voice-input-control__action voice-input-control__action--retry voice-input-control__action--retry-orange"
                                onClick={requestMicAndStartListening}
                                aria-label="Try dictation again"
                            >
                                <FiRefreshCw />
                            </button>
                        </Tooltip>
                    )}

                    {status === VOICE_STATUS.MIC_UNAVAILABLE && (
                        <Tooltip content="Try dictation again" placement="bottom">
                            <button
                                type="button"
                                className="voice-input-control__action voice-input-control__action--retry"
                                onClick={requestMicAndStartListening}
                                aria-label="Try dictation again"
                            >
                                <FiRefreshCw />
                            </button>
                        </Tooltip>
                    )}

                    {status === VOICE_STATUS.LISTENING && (
                        <Tooltip content="Send dictation" placement="bottom">
                            <button
                                type="button"
                                className="voice-input-control__action voice-input-control__action--confirm"
                                onClick={confirmTranscript}
                                aria-label="Send dictation"
                            >
                                <FiCheck />
                            </button>
                        </Tooltip>
                    )}
                </div>
            )}

            {!isVoiceActive && (
                <Tooltip
                    content={
                        isMicPermissionDenied
                            ? "Microphone access blocked"
                            : isVoiceInputSupported
                            ? "Dictate to Lou"
                            : "Dictation is not supported in this browser"
                    }
                    placement="bottom"
                >
                    <span>
                        <button
                            type="button"
                            className={getVoiceClassName(
                                "utility-btn",
                                "utility-btn--icon",
                                isMicPermissionDenied && "voice-input-control__idle-mic--blocked",
                            )}
                            aria-label={isMicPermissionDenied ? "Microphone access blocked" : "Dictate to Lou"}
                            disabled={disabled || !isVoiceInputSupported}
                            onClick={startListening}
                        >
                            {isMicPermissionDenied ? <FiMicOff /> : <FiMic />}
                        </button>
                    </span>
                </Tooltip>
            )}

            {IS_VOICE_DEBUG_ENABLED && debugRecording?.url && (
                <div className="voice-input-control__debug">
                    <button
                        type="button"
                        className="voice-input-control__debug-close"
                        onClick={clearDebugRecording}
                        aria-label="Close audio playback"
                    >
                        <FiX />
                    </button>

                    <audio controls src={debugRecording.url} />

                    <div className="voice-input-control__debug-meta">
                        <span>{Math.round(debugRecording.bytes / 1024)} KB</span>
                        {debugRecording.durationMs ? (
                            <span>{(debugRecording.durationMs / 1000).toFixed(1)}s</span>
                        ) : null}
                        <span>{debugRecording.chunkCount} chunks</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VoiceInputControl;
